<template>
  <v-container fluid class="pa-4">
    <v-row justify="center">
      <v-col cols="12" lg="10" xl="8">
        <!-- Encabezado principal -->
        <div class="d-flex align-center mb-6">
          <v-btn icon variant="text" @click="$router.back()" class="mr-3">
            <v-icon size="large">mdi-arrow-left</v-icon>
          </v-btn>
          <h2 class="text-h5 font-weight-light text-blue-grey-darken-2">
            {{ isEditing ? 'Editar Pregunta' : 'Nueva Pregunta' }}
          </h2>
        </div>

        <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
          <!-- Sección: Información General -->
          <v-card elevation="0" rounded="xl" class="mb-6 border">
            <v-card-title class="text-h6 font-weight-regular text-blue-grey-darken-1 pa-6 pb-2">
              <v-icon class="mr-2" color="blue-grey-darken-1">mdi-file-document-edit</v-icon>
              Información General
            </v-card-title>
            <v-card-subtitle class="text-caption text-blue-grey px-6 pb-4">
              Use los botones del editor para agregar fórmulas matemáticas con LaTeX
            </v-card-subtitle>

            <v-card-text class="pa-6 pt-2">
              <v-row>
                <!-- Enunciado de la pregunta -->
                <v-col cols="12">
                  <MathInput
                    v-model="form.statement"
                    label="Enunciado de la pregunta"
                    :rules="[rules.required]"
                    rows="3"
                    auto-grow
                    variant="outlined"
                    placeholder="Escriba aquí su pregunta..."
                  />
                </v-col>

                <!-- Asignatura y Grado en una fila -->
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="form.school_subject_id"
                    :items="schoolSubjects"
                    item-title="name"
                    item-value="id"
                    label="Asignatura"
                    :rules="[rules.required]"
                    variant="outlined"
                    :loading="schoolSubjectsLoading"
                    clearable
                    no-data-text="No se encontraron asignaturas"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-book-open-variant</v-icon>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.school_grade_id"
                    :items="schoolGrades"
                    item-title="gradeLabel"
                    item-value="id"
                    label="Grado"
                    :rules="[rules.required, rules.schoolGradeRequired]"
                    variant="outlined"
                    :loading="schoolGradesLoading"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-school</v-icon>
                    </template>
                  </v-select>
                </v-col>

                <!-- Botón para agregar imagen (colapsable) -->
                <v-col cols="12">
                  <v-btn
                    v-if="!statementImagePreview && !form.statement_image"
                    variant="outlined"
                    color="blue-grey"
                    size="small"
                    @click="$refs.statementImageInput.$el.querySelector('input').click()"
                  >
                    <v-icon class="mr-2">mdi-image-plus</v-icon>
                    Agregar imagen al enunciado (opcional)
                  </v-btn>

                  <v-file-input
                    ref="statementImageInput"
                    v-model="form.statement_image"
                    v-show="false"
                    accept="image/*"
                    @change="handleStatementImageChange"
                  />

                  <!-- Vista previa de imagen del enunciado -->
                  <div v-if="statementImagePreview" class="mt-2">
                    <AuthenticatedImage
                      v-if="form.existing_statement_image_url && !form.statement_image"
                      :src="statementImagePreview"
                      max-height="200"
                      image-class="border rounded"
                    />
                    <v-img
                      v-else
                      :src="statementImagePreview"
                      max-height="200"
                      contain
                      class="border rounded"
                    />
                    <v-btn
                      size="small"
                      color="error"
                      variant="text"
                      @click="removeStatementImage"
                      class="mt-2"
                    >
                      <v-icon size="small">mdi-close</v-icon>
                      Remover
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Sección: Opciones de Respuesta -->
          <v-card ref="optionsSection" elevation="0" rounded="xl" class="mb-6 border">
            <v-card-title class="text-h6 font-weight-regular text-blue-grey-darken-1 pa-6 pb-2">
              <v-icon class="mr-2" color="blue-grey-darken-1">mdi-checkbox-multiple-marked</v-icon>
              Opciones de Respuesta
            </v-card-title>
            <v-card-subtitle class="text-caption text-blue-grey px-6 pb-4">
              Llene las opciones ingresando imagen o texto y marque la opción correcta
            </v-card-subtitle>

            <v-card-text class="pa-6 pt-2">
              <v-row>
                <!-- Grid de opciones 2x2 -->
                <v-col v-for="(option, index) in form.options" :key="index" cols="12" md="6">
                  <v-card
                    elevation="0"
                    rounded="lg"
                    class="pa-4 border"
                    :class="{ 'border-success': correctOptionIndex === index }"
                  >
                    <div class="d-flex align-center mb-3">
                      <v-chip
                        :color="correctOptionIndex === index ? 'success' : 'blue-grey-lighten-3'"
                        size="small"
                        class="mr-3 font-weight-bold"
                      >
                        {{ option.label }}
                      </v-chip>
                      <v-radio-group v-model="correctOptionIndex" inline hide-details>
                        <v-radio :value="index" color="success">
                          <template v-slot:label>
                            <span class="text-body-2 text-blue-grey-darken-1">
                              Marcar como correcta
                            </span>
                          </template>
                        </v-radio>
                      </v-radio-group>
                    </div>

                    <!-- Texto de la opción -->
                    <MathInput
                      v-model="option.text"
                      :label="`Opción ${option.label}`"
                      variant="outlined"
                      density="compact"
                      rows="2"
                      class="mb-2"
                      placeholder="Escribe la respuesta..."
                    />

                    <!-- Botón para agregar imagen -->
                    <v-btn
                      v-if="!option.imagePreview"
                      variant="tonal"
                      color="blue-grey"
                      size="small"
                      @click="
                        $refs[`optionImageInput${index}`][0].$el.querySelector('input').click()
                      "
                      class="mb-3"
                    >
                      <v-icon class="mr-2">mdi-image-plus</v-icon>
                      Agregar imagen (opcional)
                    </v-btn>

                    <v-file-input
                      :ref="`optionImageInput${index}`"
                      v-model="option.imageFile"
                      v-show="false"
                      accept="image/*"
                      @change="handleOptionImageChange(index)"
                    />

                    <!-- Vista previa de imagen de opción -->
                    <div v-if="option.imagePreview">
                      <AuthenticatedImage
                        v-if="option.existingImageUrl && !option.imageFile"
                        :src="option.imagePreview"
                        max-height="120"
                        image-class="border rounded"
                      />
                      <v-img
                        v-else
                        :src="option.imagePreview"
                        max-height="120"
                        contain
                        class="border rounded"
                      />
                      <v-btn
                        size="x-small"
                        color="error"
                        variant="text"
                        @click="removeOptionImage(index)"
                        class="mt-1"
                      >
                        <v-icon size="small">mdi-close</v-icon>
                        Remover
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>

                <!-- Alertas de validación -->
                <v-col cols="12">
                  <v-alert
                    v-if="correctOptionIndex === null"
                    type="info"
                    variant="tonal"
                    density="comfortable"
                    class="mt-2"
                  >
                    Seleccione la opción correcta marcando el círculo correspondiente
                  </v-alert>

                  <v-alert v-else type="success" variant="tonal" density="comfortable" class="mt-2">
                    Respuesta correcta: Opción {{ form.options[correctOptionIndex]?.label }}
                  </v-alert>

                  <v-alert
                    v-if="hasIncompleteOptions"
                    type="warning"
                    variant="tonal"
                    density="comfortable"
                    class="mt-2"
                  >
                    Algunas opciones de respuesta están incompletas. Cada opción debe tener texto o
                    imagen.
                  </v-alert>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Sección: Justificación y Configuración (combinadas) -->
          <v-card elevation="0" rounded="xl" class="mb-6 border">
            <v-card-title class="text-h6 font-weight-regular text-blue-grey-darken-1 pa-6 pb-2">
              <v-icon class="mr-2" color="blue-grey-darken-1">mdi-cog</v-icon>
              Configuración Final
            </v-card-title>
            <v-card-subtitle class="text-caption text-blue-grey px-6 pb-4">
              Agregue la justificación de la respuesta correcta. El tiempo por defecto es de 2
              minutos
            </v-card-subtitle>

            <v-card-text class="pa-6 pt-2">
              <v-row>
                <!-- Justificación -->
                <v-col cols="12">
                  <MathInput
                    v-model="form.justification"
                    label="Justifique por qué la respuesta es correcta"
                    :rules="[rules.required]"
                    rows="3"
                    auto-grow
                    variant="outlined"
                    placeholder="Esta justificación ayudará a los estudiantes a entender..."
                  />
                </v-col>

                <!-- Imagen de la justificación -->
                <v-col cols="12">
                  <v-btn
                    v-if="!justificationImagePreview && !form.justification_image"
                    variant="outlined"
                    color="blue-grey"
                    size="small"
                    @click="$refs.justificationImageInput.$el.querySelector('input').click()"
                  >
                    <v-icon class="mr-2">mdi-image-plus</v-icon>
                    Agregar imagen a la justificación (opcional)
                  </v-btn>

                  <v-file-input
                    ref="justificationImageInput"
                    v-model="form.justification_image"
                    v-show="false"
                    accept="image/*"
                    @change="handleJustificationImageChange"
                  />

                  <div v-if="justificationImagePreview" class="mt-2">
                    <AuthenticatedImage
                      v-if="form.existing_justification_image_url && !form.justification_image"
                      :src="justificationImagePreview"
                      max-height="200"
                      image-class="border rounded"
                    />
                    <v-img
                      v-else
                      :src="justificationImagePreview"
                      max-height="200"
                      contain
                      class="border rounded"
                    />
                    <v-btn
                      size="small"
                      color="error"
                      variant="text"
                      @click="removeJustificationImage"
                      class="mt-2"
                    >
                      <v-icon size="small">mdi-close</v-icon>
                      Remover
                    </v-btn>
                  </div>
                </v-col>

                <!-- Tiempo límite -->
                <v-col cols="12" md="8">
                  <v-slider
                    v-model="form.time_limit"
                    :min="30"
                    :max="300"
                    :step="30"
                    label="Tiempo límite (segundos)"
                    thumb-label="always"
                    color="blue-grey"
                    hide-details
                  >
                    <template v-slot:thumb-label="{ modelValue }">
                      {{ formatTime(modelValue) }}
                    </template>
                  </v-slider>
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="formatTime(form.time_limit)"
                    label="Tiempo"
                    readonly
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-clock-outline"
                    hide-details
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Botones de acción al final -->
          <div class="d-flex justify-end ga-3 mt-8">
            <v-btn
              variant="outlined"
              size="large"
              @click="$router.back()"
              :disabled="loading"
              class="px-6"
            >
              Cancelar
            </v-btn>

            <v-btn
              color="blue-grey-darken-1"
              variant="flat"
              size="large"
              @click="submitForm"
              :loading="loading"
              :disabled="loading"
              class="px-6"
            >
              <v-icon class="mr-2">{{ isEditing ? 'mdi-content-save' : 'mdi-plus-circle' }}</v-icon>
              {{ isEditing ? 'Actualizar' : 'Crear' }} Pregunta
            </v-btn>
          </div>
        </v-form>
      </v-col>
    </v-row>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AuthenticatedImage from '@/components/common/AuthenticatedImage.vue'
import MathInput from '@/components/common/MathInput.vue'

export default {
  name: 'QuestionForm',

  components: {
    AuthenticatedImage,
    MathInput,
  },

  props: {
    questionId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      valid: false,
      loading: false,
      statementImagePreview: null,
      justificationImagePreview: null,
      correctOptionIndex: null,

      form: {
        statement: '',
        school_subject_id: null,
        school_grade_id: null,
        options: [
          {
            label: 'A',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
          {
            label: 'B',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
          {
            label: 'C',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
          {
            label: 'D',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
        ],
        justification: '',
        time_limit: 120,
        statement_image: null,
        existing_statement_image_url: null,
        justification_image: null,
        existing_justification_image_url: null,
      },

      difficulties: [
        { label: 'Fácil', value: 'easy' },
        { label: 'Medio', value: 'medium' },
        { label: 'Difícil', value: 'hard' },
      ],

      rules: {
        required: (value) => !!value || 'Este campo es obligatorio',
        schoolGradeRequired: (value) => !!value || 'El grado escolar es requerido',
      },

      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    }
  },

  computed: {
    ...mapGetters('questions', ['currentQuestion']),
    ...mapGetters('auth', ['isTeacher', 'isAdmin']),
    ...mapGetters('schoolGrades', {
      allGrades: 'allGrades',
      gradesLoading: 'isLoading',
    }),
    ...mapGetters('schoolSubjects', {
      allSubjects: 'allSubjects',
      subjectsLoading: 'isLoading',
    }),

    isEditing() {
      return !!this.questionId
    },

    schoolGrades() {
      return this.allGrades.map((grade) => ({
        ...grade,
        gradeLabel: `Grado ${grade.grade}`,
      }))
    },

    schoolGradesLoading() {
      return this.gradesLoading
    },

    schoolSubjects() {
      console.log(this.allSubjects)
      return this.allSubjects
    },

    schoolSubjectsLoading() {
      return this.subjectsLoading
    },

    hasIncompleteOptions() {
      return this.form.options.some((opt) => {
        const hasText = opt.text?.trim()
        const hasImageFile = opt.imageFile
        const hasExistingImage = opt.imagePreview && !hasImageFile
        return !(hasText || hasImageFile || hasExistingImage)
      })
    },
  },

  async created() {
    // Verificar permisos
    if (!this.isTeacher && !this.isAdmin) {
      this.$router.push('/questions')
      return
    }

    this.resetForm()

    // Cargar grados escolares y asignaturas disponibles
    await Promise.all([this.fetchGrades(), this.fetchAllSubjects()])

    if (this.isEditing) {
      await this.loadQuestion()
    }
  },

  beforeUnmount() {
    this.resetForm()
  },

  methods: {
    ...mapActions('questions', ['fetchQuestionById', 'createQuestion', 'updateQuestion']),
    ...mapActions('schoolGrades', ['fetchGrades']),
    ...mapActions('schoolSubjects', ['fetchAllSubjects']),

    async loadQuestion() {
      this.loading = true
      this.resetForm()

      const result = await this.fetchQuestionById(this.questionId)

      if (result.success) {
        this.fillForm(this.currentQuestion)
      } else {
        this.showSnackbar(result.error, 'error')
        this.$router.push('/questions')
      }

      this.loading = false
    },

    fillForm(question) {
      this.form = {
        statement: question.statement || '',
        school_subject_id: question.school_subject_id || question.school_subject?.id || null,
        school_grade_id: question.school_grade_id || question.school_grade?.id || null,
        options: this.normalizeOptions(question.options || []),
        justification: question.justification || '',
        time_limit: question.time_limit || 120,
        statement_image: null,
        existing_statement_image_url: question.statement_image_url || null,
        justification_image: null,
        existing_justification_image_url: question.justification_image_url || null,
      }

      // Encontrar la opción correcta
      this.correctOptionIndex = question.options?.findIndex((opt) => opt.is_correct) ?? null

      // Si hay imagen del enunciado existente, mostrar vista previa
      if (question.statement_image_url) {
        this.statementImagePreview = question.statement_image_url
      }

      // Si hay imagen de justificación existente, mostrar vista previa
      if (question.justification_image_url) {
        this.justificationImagePreview = question.justification_image_url
      }
    },

    normalizeOptions(options) {
      const labels = ['A', 'B', 'C', 'D']
      return labels.map((label, index) => {
        const option = options[index] || {}
        return {
          id: option.id || null,
          label,
          text: option.text || '',
          imageFile: null,
          imagePreview: option.image_url || null,
          existingImageUrl: option.image_url || null, // Guardar URL existente
          is_correct: option.is_correct || false,
        }
      })
    },

    handleStatementImageChange() {
      const file = this.form.statement_image?.[0] || this.form.statement_image

      if (file) {
        // Validar tamaño (5MB máximo)
        if (file.size > 5 * 1024 * 1024) {
          this.showSnackbar('La imagen debe ser menor a 5MB', 'error')
          this.form.statement_image = null
          return
        }

        // Validar tipo
        if (!file.type.startsWith('image/')) {
          this.showSnackbar('El archivo debe ser una imagen', 'error')
          this.form.statement_image = null
          return
        }

        // Al subir nueva imagen, eliminar referencia a imagen existente
        this.form.existing_statement_image_url = null

        // Crear vista previa
        const reader = new FileReader()
        reader.onload = (e) => {
          this.statementImagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    removeStatementImage() {
      this.form.statement_image = null
      this.statementImagePreview = null
      this.form.existing_statement_image_url = null // También remover URL existente
    },

    handleJustificationImageChange() {
      const file = this.form.justification_image?.[0] || this.form.justification_image

      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          this.showSnackbar('La imagen debe ser menor a 5MB', 'error')
          this.form.justification_image = null
          return
        }

        if (!file.type.startsWith('image/')) {
          this.showSnackbar('El archivo debe ser una imagen', 'error')
          this.form.justification_image = null
          return
        }

        this.form.existing_justification_image_url = null

        const reader = new FileReader()
        reader.onload = (e) => {
          this.justificationImagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    removeJustificationImage() {
      this.form.justification_image = null
      this.justificationImagePreview = null
      this.form.existing_justification_image_url = null
    },

    handleOptionImageChange(index) {
      const file = this.form.options[index].imageFile?.[0] || this.form.options[index].imageFile

      if (file) {
        // Validar tamaño (5MB máximo)
        if (file.size > 5 * 1024 * 1024) {
          this.showSnackbar('La imagen debe ser menor a 5MB', 'error')
          this.form.options[index].imageFile = null
          return
        }

        // Validar tipo
        if (!file.type.startsWith('image/')) {
          this.showSnackbar('El archivo debe ser una imagen', 'error')
          this.form.options[index].imageFile = null
          return
        }

        // Al subir nueva imagen, eliminar referencia a imagen existente
        this.form.options[index].existingImageUrl = null

        // Crear vista previa
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.options[index].imagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    removeOptionImage(index) {
      this.form.options[index].imageFile = null
      this.form.options[index].imagePreview = null
      this.form.options[index].existingImageUrl = null // También remover URL existente
    },

    async submitForm() {
      // 1. Disparar validación de Vuetify en todos los campos con :rules
      const { valid } = await this.$refs.form.validate()
      if (!valid) {
        this.scrollToFirstError()
        return
      }

      // 2. Validar selección de opción correcta (sin regla de Vuetify)
      if (this.correctOptionIndex === null) {
        this.showSnackbar(
          'Debe seleccionar la opción correcta marcando el círculo correspondiente',
          'error',
        )
        this.$refs.optionsSection?.$el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }

      // Validar que cada opción tenga texto O imagen
      const invalidOptions = this.form.options.filter((opt, index) => {
        const hasText = opt.text?.trim()
        const hasImageFile = opt.imageFile
        const hasExistingImage = opt.imagePreview && !hasImageFile // Imagen existente en edición
        return !(hasText || hasImageFile || hasExistingImage)
      })

      if (invalidOptions.length > 0) {
        const labels = invalidOptions.map((opt) => `opción ${opt.label}`).join(', ')
        this.showSnackbar(`Debe completar la ${labels} con texto o imagen`, 'error')
        return
      }

      // Validar que al menos haya 2 opciones con contenido
      const validOptions = this.form.options.filter((opt) => {
        const hasText = opt.text?.trim()
        const hasImageFile = opt.imageFile
        const hasExistingImage = opt.imagePreview && !hasImageFile
        return hasText || hasImageFile || hasExistingImage
      })

      if (validOptions.length < 2) {
        this.showSnackbar('Debe tener al menos 2 opciones válidas', 'error')
        return
      }

      this.loading = true

      // Actualizar la propiedad is_correct basada en la selección
      this.form.options.forEach((option, index) => {
        option.is_correct = index === this.correctOptionIndex
      })

      // Preparar datos para la nueva API
      const questionData = {
        statement: this.form.statement.trim(),
        school_subject_id: this.form.school_subject_id,
        school_grade_id: this.form.school_grade_id,
        justification: this.form.justification.trim(),
        time_limit: this.form.time_limit,
        options: this.form.options
          .map((option) => {
            const optionData = {
              label: option.label,
              text: option.text?.trim() || null,
              is_correct: option.is_correct,
            }

            if (option.id) {
              optionData.id = option.id
            }

            // Solo incluir image o existing_image_url, nunca ambos
            if (option.imageFile) {
              optionData.image = option.imageFile?.[0] || option.imageFile
            } else if (option.existingImageUrl) {
              optionData.existing_image_url = option.existingImageUrl
            }

            return optionData
          })
          .filter((option) => option.text || option.image || option.existing_image_url || option.id), // Solo enviar opciones con contenido
      }

      // Agregar imagen del enunciado - solo enviar una, no ambas
      if (this.form.statement_image) {
        const file = this.form.statement_image?.[0] || this.form.statement_image
        questionData.statement_image = file
      } else if (this.form.existing_statement_image_url) {
        questionData.existing_statement_image_url = this.form.existing_statement_image_url
      }

      // Agregar imagen de la justificación - solo enviar una, no ambas
      if (this.form.justification_image) {
        const file = this.form.justification_image?.[0] || this.form.justification_image
        questionData.justification_image = file
      } else if (this.form.existing_justification_image_url) {
        questionData.existing_justification_image_url = this.form.existing_justification_image_url
      }

      let result
      if (this.isEditing) {
        result = await this.updateQuestion({
          questionId: this.questionId,
          questionData,
        })
      } else {
        console.log('Creating question with data:', questionData)
        result = await this.createQuestion(questionData)
      }

      this.loading = false

      if (result.success) {
        this.showSnackbar(
          `Pregunta ${this.isEditing ? 'actualizada' : 'creada'} exitosamente`,
          'success',
        )
        setTimeout(() => {
          this.$router.push('/questions')
        }, 1500)
      } else {
        this.showSnackbar(result.error, 'error')
      }
    },

    resetForm() {
      // Resetear todos los estados del formulario
      this.valid = false
      this.loading = false
      this.statementImagePreview = null
      this.justificationImagePreview = null
      this.correctOptionIndex = null

      this.form = {
        statement: '',
        school_subject_id: null,
        school_grade_id: null,
        options: [
          {
            label: 'A',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
          {
            label: 'B',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
          {
            label: 'C',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
          {
            label: 'D',
            text: '',
            imageFile: null,
            imagePreview: null,
            existingImageUrl: null,
            is_correct: false,
          },
        ],
        justification: '',
        time_limit: 120,
        statement_image: null,
        existing_statement_image_url: null,
        justification_image: null,
        existing_justification_image_url: null,
      }
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const formEl = this.$refs.form?.$el
        if (!formEl) return
        const firstError = this.$refs.form.$el.querySelector('.v-input--error, .v-field--error')
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
          firstError.classList.add('field-error-shake')
          setTimeout(() => firstError.classList.remove('field-error-shake'), 600)
        }
      })
    },

    showSnackbar(message, color = 'success', timeout = 4000) {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout,
      }
    },
  },
}
</script>

<style>
/* Animación global: se aplica al elemento del DOM vía classList */
@keyframes field-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-6px);
  }
  40% {
    transform: translateX(6px);
  }
  60% {
    transform: translateX(-4px);
  }
  80% {
    transform: translateX(4px);
  }
}

.field-error-shake {
  animation: field-shake 0.5s ease-in-out;
}
</style>

<style scoped>
.v-slider {
  margin-top: 16px;
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.rounded {
  border-radius: 4px;
}
</style>
